  "use client";

  import React, {
    useMemo,
    useState,
    useEffect,
  } from "react";
  import { useNavigate } from "react-router-dom";
  import "../../../../style/PaymentInvoice.css";

  import DataTable, {
    TableColumn,
    ConditionalStyles,
  } from "react-data-table-component";

  import DatePicker from "react-datepicker";
  import "react-datepicker/dist/react-datepicker.css";
  import { apiClient } from "../../../../api/apiClient";

  import {
    type LucideIcon,
    Search,
    CalendarRange,
    ChevronDown,
    Eye,
    Wallet,
    Truck,
    Paintbrush,
    Cog,
    Users,
    X,
    CheckCircle2,
    Layers,
    ArrowUpRight,
    Download,
    FileText,
    CalendarDays,
    Hash,
    IndianRupee,
    PackageCheck,
    Calendar,
  } from "lucide-react";
  import { ROUTES } from "../../../../Routes/routes";

  type Role = "Designer" | "Milling Hub" | "Delivery";

  interface PaymentUser {
    userID: number;
    name: string;
    roleID: number;
  }
  interface InvoiceCase {
    workPaymentID: number;
    caseId: number;
    orderId?: number | null;
    description: string;
    caseDate: string | null;
    amount: number;
  }

  interface Invoice {
    invoiceID: number;
    id: string;

    userID: number;
    person: string;
    email: string;

    roleID: number;
    role: Role;

    startDate: string;
    endDate: string;

    period: string;

    payoutDate: string;
    payoutDateRaw: string;

    cases: number;
    paid: number;

    status: string;
    emailSent: boolean;

    caseDetails?: InvoiceCase[];
  }



  const TABS: {
    key: "All" | Role;
    label: string;
    icon: LucideIcon;
  }[] = [
    {
      key: "All",
      label: "All",
      icon: Wallet,
    },
    {
      key: "Designer",
      label: "Designer",
      icon: Paintbrush,
    },
    {
      key: "Milling Hub",
      label: "Milling Hub",
      icon: Cog,
    },
    {
      key: "Delivery",
      label: "Delivery",
      icon: Truck,
    },
  ];

  const ROLE_META: Record<
    Role,
    {
      badge: string;
      row: string;
      icon: LucideIcon;
    }
  > = {
    Designer: {
      badge: "pi-role-badge--designer",
      row: "pi-row--designer",
      icon: Paintbrush,
    },

    "Milling Hub": {
      badge: "pi-role-badge--milling-hub",
      row: "pi-row--milling-hub",
      icon: Cog,
    },

    Delivery: {
      badge: "pi-role-badge--delivery",
      row: "pi-row--delivery",
      icon: Truck,
    },
  };

  const currency = (n: number) =>
    "₹" +
    n.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });

  const formatDate = (value?: string | null) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  export default function PaymentInvoices() {
    const navigate = useNavigate();

  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const [users, setUsers] = useState<PaymentUser[]>([]);

  const [loading, setLoading] = useState(false);
  const [loadingCases, setLoadingCases] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [activeTab, setActiveTab] =
    useState<"All" | Role>("All");

  const [query, setQuery] = useState("");

  const [teamMember, setTeamMember] = useState("");

  const [selectedUserID, setSelectedUserID] =
    useState<number | null>(null);

  const [memberSearch, setMemberSearch] =
    useState("");

  const [memberOpen, setMemberOpen] =
    useState(false);

  const [activeInvoice, setActiveInvoice] =
    useState<Invoice | null>(null);

    /*
    * DATE RANGE
    */
    const [dateRange, setDateRange] = useState<
      [Date | null, Date | null]
    >([null, null]);

    const [rangeStart, rangeEnd] = dateRange;
  useEffect(() => {
    loadInvoices();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);

      const response = await apiClient.get<any>(
        "/Payment/payment-tracking/users"
      );

      console.log(
        "PAYMENT USERS RESPONSE:",
        response
      );

      console.log(
        "PAYMENT USERS DATA:",
        response.data
      );

      const data =
        response.data?.data ??
        response.data?.Data ??
        response.data ??
        [];

      const mappedUsers: PaymentUser[] =
        Array.isArray(data)
          ? data.map((item: any) => ({
              userID: Number(
                item.userID ??
                item.UserID ??
                0
              ),

              name:
                item.name ??
                item.Name ??
                "",

              roleID: Number(
                item.roleID ??
                item.RoleID ??
                0
              ),
            }))
          : [];

      console.log(
        "MAPPED PAYMENT USERS:",
        mappedUsers
      );

      setUsers(mappedUsers);

    } catch (error) {
      console.error(
        "Failed to load payment users:",
        error
      );
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);

      const response = await apiClient.get<any>(
        "/Payment/payment-invoices"
      );

      console.log("PAYMENT INVOICE FULL RESPONSE:", response);
      console.log("PAYMENT INVOICE RESPONSE DATA:", response.data);

      // Handle both possible response structures
      const data =
        response.data?.data ??
        response.data?.Data ??
        response.data ??
        [];

      console.log("PAYMENT INVOICE FINAL DATA:", data);

      const mappedInvoices: Invoice[] = Array.isArray(data)
        ? data.map((item: any) => ({
            invoiceID: Number(item.invoiceID ?? item.InvoiceID),

            id:
              item.invoiceNo ??
              item.InvoiceNo ??
              "",

            userID: Number(
              item.userID ?? item.UserID ?? 0
            ),

            person:
              item.userName ??
              item.UserName ??
              "",

            email:
              item.email ??
              item.Email ??
              "",

            roleID: Number(
              item.roleID ?? item.RoleID ?? 0
            ),

            role: (
              item.roleName ??
              item.RoleName ??
              ""
            ) as Role,

            startDate:
              item.startDate ??
              item.StartDate ??
              "",

            endDate:
              item.endDate ??
              item.EndDate ??
              "",

            period: `${formatDate(
              item.startDate ?? item.StartDate
            )} – ${formatDate(
              item.endDate ?? item.EndDate
            )}`,

            payoutDate: formatDate(
              item.payoutDate ?? item.PayoutDate
            ),

            payoutDateRaw:
              item.payoutDate ??
              item.PayoutDate ??
              "",

            cases: Number(
              item.totalCases ??
              item.TotalCases ??
              0
            ),

            paid: Number(
              item.totalAmount ??
              item.TotalAmount ??
              0
            ),

            status:
              item.status ??
              item.Status ??
              "",

            emailSent: Boolean(
              item.emailSent ??
              item.EmailSent ??
              false
            ),
          }))
        : [];

      console.log("MAPPED INVOICES:", mappedInvoices);

      setInvoices(mappedInvoices);

    } catch (error) {
      console.error(
        "Failed to load payment invoices:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (invoice: Invoice) => {
    try {
      setLoadingCases(true);

      setActiveInvoice({
        ...invoice,
        caseDetails: [],
      });

      const response = await apiClient.get<any>(
        `/Payment/payment-invoices/${invoice.invoiceID}/cases`
      );

      console.log("CASE DETAILS FULL RESPONSE:", response);
      console.log(
        "CASE DETAILS RESPONSE DATA:",
        response.data
      );

      const data =
        response.data?.data ??
        response.data?.Data ??
        response.data ??
        [];

      console.log("CASE DETAILS FINAL DATA:", data);

      const caseDetails: InvoiceCase[] =
        Array.isArray(data)
          ? data.map((item: any) => ({
              workPaymentID: Number(
                item.workPaymentID ??
                item.WorkPaymentID ??
                0
              ),

              caseId: Number(
                item.caseID ??
                item.CaseID ??
                0
              ),

              orderId:
                item.orderID ??
                item.OrderID ??
                null,

              description:
                item.description ??
                item.Description ??
                `Case #${
                  item.caseID ??
                  item.CaseID ??
                  ""
                }`,

              caseDate:
                item.caseDate ??
                item.CaseDate ??
                null,

              amount: Number(
                item.amount ??
                item.Amount ??
                0
              ),
            }))
          : [];

      console.log(
        "MAPPED CASE DETAILS:",
        caseDetails
      );

      setActiveInvoice({
        ...invoice,
        caseDetails,
      });
    } catch (error) {
      console.error(
        "Failed to load invoice case details:",
        error
      );
    } finally {
      setLoadingCases(false);
    }
  };


/*
 * TEAM MEMBERS
 */
const members = useMemo(() => {
  const roleIdMap: Record<Role, number> = {
    "Milling Hub": 3,
    Designer: 6,
    Delivery: 7,
  };

  if (activeTab === "All") {
    return users;
  }

  return users.filter(
    (user) => user.roleID === roleIdMap[activeTab]
  );
}, [users, activeTab]);

/*
 * SEARCHABLE TEAM MEMBERS
 */
const filteredMembers = useMemo(() => {
  const searchText = memberSearch.trim().toLowerCase();

  if (!searchText) {
    return members;
  }

  return members.filter((member) =>
    member.name.toLowerCase().includes(searchText)
  );
}, [members, memberSearch]);

    /*
    * CLEAR DATE RANGE
    */
    const clearDateRange = () => {
      setDateRange([null, null]);
    };

    /*
    * FILTER DATA
    */
    const filtered = useMemo(() => {
    return invoices.filter((invoice) => {
        /*
        * ROLE
        */
        if (
          activeTab !== "All" &&
          invoice.role !== activeTab
        ) {
          return false;
        }

        /*
        * TEAM MEMBER
        */
   
if (
  selectedUserID !== null &&
  invoice.userID !== selectedUserID
) {
  return false;
}

        /*
        * SEARCH
        */
      if (
    query &&
    !`${invoice.id}
      ${invoice.person}
      ${invoice.email}
      ${invoice.period}
      ${invoice.role}
      ${invoice.status}
      ${invoice.cases}
      ${invoice.paid}`
      .toLowerCase()
      .includes(query.toLowerCase())
  ) {
    return false;
  }

        /*
        * DATE RANGE
        *
        * Filtering is done using payoutDate.
        */
      if (rangeStart || rangeEnd) {
    const invoiceDate = new Date(invoice.payoutDateRaw);

    invoiceDate.setHours(0, 0, 0, 0);

    if (rangeStart) {
      const start = new Date(rangeStart);
      start.setHours(0, 0, 0, 0);

      if (invoiceDate < start) {
        return false;
      }
    }

    if (rangeEnd) {
      const end = new Date(rangeEnd);
      end.setHours(23, 59, 59, 999);

      if (invoiceDate > end) {
        return false;
      }
    }
  }

        return true;
      });
    }, [
        invoices,
  activeTab,
  selectedUserID,
  query,
  rangeStart,
  rangeEnd,
    ]);

    /*
    * SUMMARY
    */
    const totalPaid = filtered.reduce(
      (sum, invoice) => sum + invoice.paid,
      0
    );

    const totalCases = filtered.reduce(
      (sum, invoice) => sum + invoice.cases,
      0
    );

    /*
    * TAB COUNTS
    */
  const tabCount = (key: "All" | Role) => {
    if (key === "All") {
      return invoices.length;
    }

    return invoices.filter(
      (invoice) => invoice.role === key
    ).length;
  };

    /*
    * CASE DETAILS
    */
  

    /*
    * DOWNLOAD
    */
 const handleDownloadInvoice = async (invoice: Invoice) => {
  try {
    setLoadingCases(true);

    // Get case details before opening printout
    const response = await apiClient.get<any>(
      `/Payment/payment-invoices/${invoice.invoiceID}/cases`
    );

    const data =
      response.data?.data ??
      response.data?.Data ??
      response.data ??
      [];

    const caseDetails: InvoiceCase[] =
      Array.isArray(data)
        ? data.map((item: any) => ({
            workPaymentID: Number(
              item.workPaymentID ??
              item.WorkPaymentID ??
              0
            ),

            caseId: Number(
              item.caseID ??
              item.CaseID ??
              0
            ),

            orderId:
              item.orderID ??
              item.OrderID ??
              null,

            description:
              item.description ??
              item.Description ??
              `Case #${
                item.caseID ??
                item.CaseID ??
                ""
              }`,

            caseDate:
              item.caseDate ??
              item.CaseDate ??
              null,

            amount: Number(
              item.amount ??
              item.Amount ??
              0
            ),
          }))
        : [];

    // Create complete invoice object
    const completeInvoice: Invoice = {
      ...invoice,
      caseDetails,
    };

    // Store complete invoice
    sessionStorage.setItem(
      "invoicePrintout",
      JSON.stringify(completeInvoice)
    );

    // Open printout page
    navigate(
      ROUTES.CENTRAL.CENTRAL_InvoicePrintout
    );

  } catch (error) {
    console.error(
      "Failed to prepare invoice PDF:",
      error
    );
  } finally {
    setLoadingCases(false);
  }
};

    /*
    * MAIN TABLE COLUMNS
    */
    const invoiceColumns: TableColumn<Invoice>[] = [
      {
        name: "Invoice No",
        selector: (row) => row.id,
        sortable: true,
        width: "170px",
        cell: (row) => (
          <span className="pi-dt-invoice-id">
            {row.id}
          </span>
        ),
      },

      {
        name: "Person",
        selector: (row) => row.person,
        sortable: true,
        minWidth: "220px",
        cell: (row) => (
          <div className="pi-dt-person">
            <div className="pi-dt-avatar">
              {row.person.slice(0, 1).toUpperCase()}
            </div>

            <div className="pi-dt-person-info">
              <span className="pi-dt-person-name">
                {row.person}
              </span>

              <span
                className={`pi-role-badge ${
                  ROLE_META[row.role].badge
                }`}
              >
                <span className="pi-role-dot" />

                {row.role}
              </span>
            </div>
          </div>
        ),
      },

      {
        name: "Period",
        selector: (row) => row.period,
        sortable: true,
        minWidth: "210px",
        cell: (row) => (
          <span className="pi-dt-period">
            {row.period}
          </span>
        ),
      },

      {
        name: "Payout Date",
        selector: (row) => row.payoutDate,
        sortable: true,
        minWidth: "150px",
        cell: (row) => (
          <span className="pi-dt-date">
            {row.payoutDate}
          </span>
        ),
      },

      {
        name: "Cases",
        selector: (row) => row.cases,
        sortable: true,
        width: "95px",
        center: true,
        cell: (row) => (
          <span className="pi-dt-cases">
            {row.cases}
          </span>
        ),
      },

      {
        name: "Paid",
        selector: (row) => row.paid,
        sortable: true,
        right: true,
        width: "125px",
        cell: (row) => (
          <span className="pi-paid">
            {currency(row.paid)}
          </span>
        ),
      },

      {
        name: "Action",
        width: "120px",
        right: true,
        cell: (row) => (
          <button
            type="button"
            className="pi-view-btn"
            onClick={() =>
              handleViewInvoice(row)
            }
          >
            <Eye size={14} />
            View
          </button>
        ),
      },
    ];

    /*
    * POPUP CASE TABLE COLUMNS
    */
    const caseColumns: TableColumn<InvoiceCase>[] = [
      {
        name: "Case ID",
        selector: (row) => row.caseId,
        sortable: true,
        width: "130px",
        cell: (row) => (
          <span className="pi-case-id">
            {row.caseId}
          </span>
        ),
      },

      {
        name: "Paid To",
        selector: (row) => row.description,
        sortable: true,
        minWidth: "220px",
        cell: (row) => (
          <span className="pi-case-description">
            {row.description}
          </span>
        ),
      },

  {
    name: "Case Date",
    selector: (row) =>
      row.caseDate
        ? new Date(row.caseDate).getTime()
        : 0,
    sortable: true,
    width: "150px",
    cell: (row) => (
      <span className="pi-case-date">
        {formatDate(row.caseDate)}
      </span>
    ),
  },

      {
        name: "Amount",
        selector: (row) => row.amount,
        sortable: true,
        right: true,
        width: "140px",
        cell: (row) => (
          <span className="pi-case-amount">
            {currency(row.amount)}
          </span>
        ),
      },
    ];

    /*
    * MAIN DATATABLE STYLES
    */
    const invoiceTableStyles = {
      table: {
        style: {
          width: "100%",
        },
      },

      headRow: {
        style: {
          minHeight: "50px",
          backgroundColor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        },
      },

      headCells: {
        style: {
          color: "#64748b",
          fontSize: "11px",
          fontWeight: "700",
          textTransform: "uppercase" as const,
          letterSpacing: "0.04em",
          paddingLeft: "18px",
          paddingRight: "18px",
        },
      },

      rows: {
        style: {
          minHeight: "70px",
          borderBottom: "1px solid #f1f5f9",
          color: "#334155",
          fontSize: "13px",
          transition: "background-color 0.15s ease",
        },

        highlightOnHoverStyle: {
          backgroundColor: "#f8fbff",
          cursor: "default",
        },
      },

      cells: {
        style: {
          paddingLeft: "18px",
          paddingRight: "18px",
        },
      },

      pagination: {
        style: {
          minHeight: "58px",
          borderTop: "1px solid #e2e8f0",
          backgroundColor: "#fff",
          paddingLeft: "18px",
          paddingRight: "18px",
          color: "#64748b",
          fontSize: "12px",
        },

        pageButtonsStyle: {
          borderRadius: "7px",
          height: "32px",
          width: "32px",
          padding: "4px",
          margin: "0 3px",
          cursor: "pointer",
          transition: "all 0.15s ease",
        },
      },
    };

    /*
    * POPUP DATATABLE STYLES
    */
    const caseTableStyles = {
      table: {
        style: {
          width: "100%",
        },
      },

      headRow: {
        style: {
          minHeight: "46px",
          backgroundColor: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
        },
      },

      headCells: {
        style: {
          color: "#64748b",
          fontSize: "10.5px",
          fontWeight: "700",
          textTransform: "uppercase" as const,
          letterSpacing: "0.04em",
          paddingLeft: "14px",
          paddingRight: "14px",
        },
      },

      rows: {
        style: {
          minHeight: "54px",
          borderBottom: "1px solid #f1f5f9",
          fontSize: "13px",
          color: "#475569",
        },

        highlightOnHoverStyle: {
          backgroundColor: "#f8fbff",
        },
      },

      cells: {
        style: {
          paddingLeft: "14px",
          paddingRight: "14px",
        },
      },

      pagination: {
        style: {
          minHeight: "54px",
          borderTop: "1px solid #e2e8f0",
          backgroundColor: "#fff",
          color: "#64748b",
          fontSize: "12px",
        },

        pageButtonsStyle: {
          borderRadius: "7px",
          height: "30px",
          width: "30px",
          padding: "4px",
          margin: "0 2px",
          cursor: "pointer",
        },
      },
    };

    return (
      <div className="pi-page">
        <div className="pi-container">

          {/* =====================================================
              HEADER
          ===================================================== */}

          <div className="pi-header">
            {/* <div className="pi-header-icon">
              <Wallet
                size={22}
                strokeWidth={2.2}
              />
            </div> */}

            <div>
              <h1 className="pi-title">
                Payment Invoices
              </h1>

              <p className="pi-subtitle">
                Every invoice raised for your team,
                by role and billing period.
              </p>
            </div>
          </div>

          {/* =====================================================
              SUMMARY
          ===================================================== */}

          <div className="pi-stat-grid">

            <div className="pi-stat-card">
              <div className="pi-stat-top">
                <span className="pi-stat-label">
                  Total invoices
                </span>

                <span className="pi-stat-chip pi-stat-chip--blue">
                  <Layers size={16} />
                </span>
              </div>

              <div className="pi-stat-value">
                {filtered.length}
              </div>

              {/* <div className="pi-stat-caption">
                <Users size={13} />
                {members.length} team members
              </div> */}
            </div>

            <div className="pi-stat-card">
              <div className="pi-stat-top">
                <span className="pi-stat-label">
                  Cases billed
                </span>

                <span className="pi-stat-chip pi-stat-chip--violet">
                  <PackageCheck size={16} />
                </span>
              </div>

              <div className="pi-stat-value">
                {totalCases}
              </div>

          
            </div>

            <div className="pi-stat-card">
              <div className="pi-stat-top">
                <span className="pi-stat-label">
                  Total paid
                </span>

                <span className="pi-stat-chip pi-stat-chip--green">
                  <CheckCircle2 size={16} />
                </span>
              </div>

              <div className="pi-stat-value">
                {currency(totalPaid)}
              </div>

            
            </div>

          </div>

        

          <div className="pi-card">

    <div className="pi-toolbar">
    <div className="pi-tabs">
      {TABS.map(({ key, label }) => {
        const active = activeTab === key;

        return (
          <button
            key={key}
            type="button"
          onClick={() => {
    setActiveTab(key);

    setTeamMember("");
    setSelectedUserID(null);
    setMemberSearch("");
    setMemberOpen(false);
  }}
            className={`pi-tab ${
              active ? "pi-tab--active" : ""
            }`}
          >
            <span>{label}</span>
          </button>
        );
      })}
    </div>

    {/* ================= FILTERS ================= */}
    <div className="pi-filters">

      {/* DATE RANGE */}
    <div className="pi-date-range">
    <div className="pi-date-input-wrapper">
      <i className="bi bi-calendar3 pi-calendar-icon"></i>

      <DatePicker
        selectsRange
        startDate={rangeStart}
        endDate={rangeEnd}
        onChange={(update) => {
          const [newStart, newEnd] = update as [
            Date | null,
            Date | null
          ];

          setDateRange([newStart, newEnd]);
        }}
        isClearable
        placeholderText="Select date range"
        dateFormat="dd MMM yyyy"
        className="pi-date-input"
        maxDate={new Date()}
      />
    </div>

    {(rangeStart || rangeEnd) && (
      <button
        type="button"
        className="pi-date-clear"
        onClick={clearDateRange}
      >
        <i className="bi bi-x-circle me-1"></i>
      
      </button>
    )}
  </div>

      {/* TEAM MEMBER */}
   {/* TEAM MEMBER */}
<div className="pi-member-dropdown">

  <button
    type="button"
    onClick={() => {
      setMemberOpen((open) => !open);
    }}
    className={`pi-filter-control ${
      memberOpen
        ? "pi-filter-control--active"
        : ""
    }`}
  >
    <span className="pi-filter-control-left">
      {teamMember || "Select team member"}
    </span>

    <span className="pi-dropdown-arrow">
      {memberOpen ? "▲" : "▼"}
    </span>
  </button>

  {memberOpen && (
    <div className="pi-member-menu">

      {/* SEARCH TEAM MEMBER */}
      <div className="pi-member-search">
        <Search size={14} />

        <input
          type="text"
          value={memberSearch}
          onChange={(e) =>
            setMemberSearch(e.target.value)
          }
          placeholder="Search team member..."
          autoFocus
        />
      </div>

      {/* ALL MEMBERS */}
      <button
        type="button"
        className={`pi-member-item ${
          !teamMember
            ? "pi-member-item--selected"
            : ""
        }`}
        onClick={() => {
          setTeamMember("");
          setSelectedUserID(null);
          setMemberSearch("");
          setMemberOpen(false);
        }}
      >
        <span>All members</span>

        {!teamMember && (
          <span>✓</span>
        )}
      </button>

      {/* LOADING */}
      {loadingUsers ? (
        <div className="pi-member-item">
          Loading team members...
        </div>
      ) : filteredMembers.length === 0 ? (
        /* NO RESULT */
        <div className="pi-member-item">
          No team member found
        </div>
      ) : (
        /* TEAM MEMBERS */
        filteredMembers.map((member) => (
          <button
            type="button"
            key={`${member.userID}_${member.roleID}`}
            className={`pi-member-item ${
              selectedUserID === member.userID
                ? "pi-member-item--selected"
                : ""
            }`}
            onClick={() => {
              setTeamMember(member.name);
              setSelectedUserID(member.userID);
              setMemberSearch("");
              setMemberOpen(false);
            }}
          >
            <span>
              {member.name}
            </span>

            {selectedUserID === member.userID && (
              <span>✓</span>
            )}
          </button>
        ))
      )}

    </div>
  )}

</div>
      {/* SEARCH */}
    

    </div>
    
  </div>
  <div className="row py-2">
      <div className="col-md-9">

      </div>
      <div className="col-md-3">
          <div className="pi-search-box">

        <input
          type="text"
          placeholder="Search invoice..."
          value={query}
          onChange={(e) =>
            setQuery(e.target.value)
          }
        />

        {query && (
          <button
            type="button"
            className="pi-search-clear"
            onClick={() => setQuery("")}
          >
            Clear
          </button>
        )}

      </div> 
      </div>
  </div>
  
          

            <div className="pi-table-heading">
              <div>
                <h2>
                  Team invoices
                </h2>

                <p>
                  View payment invoices and
                  case-wise billing details.
                </p>
              </div>

              <div className="pi-table-heading-count">
                <Layers size={14} />

                {filtered.length} invoices
              </div>
            </div>

            {/* ===================================================
                MAIN DATATABLE
            =================================================== */}

            <div className="pi-main-datatable">
            <DataTable
    columns={invoiceColumns}
    data={filtered}
    progressPending={loading}
    progressComponent={
      <div className="py-4">
        Loading invoices...
      </div>
    }
    pagination
                paginationPerPage={5}
                paginationRowsPerPageOptions={[
                  5,
                  10,
                  15,
                  20,
                ]}
                highlightOnHover
                responsive
                striped={false}
                fixedHeader
                fixedHeaderScrollHeight="470px"
                persistTableHead
                customStyles={
                  invoiceTableStyles
                }
                noDataComponent={
                  <div className="pi-empty">
                    <div className="pi-empty-icon">
                      <Layers
                        size={22}
                      />
                    </div>

                    <strong>
                      No invoices found
                    </strong>

                    <span>
                      Try changing your
                      filters or search.
                    </span>
                  </div>
                }
              />
            </div>

            {/* ===================================================
                FOOTER
            =================================================== */}

            {/* <div className="pi-footer">

              <span>
                Showing{" "}
                <strong>
                  {filtered.length}
                </strong>{" "}
                of{" "}
                {INVOICES.length} invoices
              </span>

              <span>
                Total paid:{" "}
                <strong className="pi-footer-amount">
                  {currency(totalPaid)}
                </strong>
              </span>

            </div> */}
          </div>
        </div>

        {/* =====================================================
            INVOICE MODAL
        ===================================================== */}

        {activeInvoice && (
          <div
            className="pi-modal-overlay"
            onClick={() =>
              setActiveInvoice(null)
            }
          >
            <div
              className="pi-invoice-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              {/* =================================================
                  MODAL HEADER
              ================================================= */}

              <div className="pi-invoice-modal-top">

                <div className="pi-invoice-modal-title-wrap">

                  <div className="pi-invoice-modal-icon">
                    <FileText size={19} />
                  </div>

                  <div>
                    <p className="pi-invoice-modal-eyebrow">
                      Invoice preview
                    </p>

                    <h2 className="pi-invoice-modal-title">
                      {activeInvoice.id}
                    </h2>
                  </div>

                </div>

                <button
                  type="button"
                  className="pi-modal-close"
                  onClick={() =>
                    setActiveInvoice(null)
                  }
                  aria-label="Close invoice"
                >
                  <X size={18} />
                </button>

              </div>

              {/* =================================================
                  DOCUMENT
              ================================================= */}

              <div className="pi-invoice-document">

                {/* Heading */}

                <div className="pi-invoice-heading">

                  <div>
                    <span className="pi-invoice-label">
                      PAYMENT INVOICE
                    </span>

                    <h2>
                      {activeInvoice.id}
                    </h2>
                  </div>

                  <div className="pi-invoice-paid-status">
                    <CheckCircle2 size={15} />
                    Paid
                  </div>

                </div>

                {/* Person */}

                <div className="pi-invoice-party">

                  <div className="pi-invoice-person">

                    <div className="pi-invoice-person-avatar">
                      {activeInvoice.person
                        .slice(0, 1)
                        .toUpperCase()}
                    </div>

                    <div>
                      <span className="pi-detail-label">
                        Team Member
                      </span>

                      <h3>
                        {activeInvoice.person}
                      </h3>

                      <span
                        className={`pi-role-badge ${
                          ROLE_META[
                            activeInvoice
                              .role
                          ].badge
                        }`}
                      >
                        <span className="pi-role-dot" />

                        {
                          activeInvoice.role
                        }
                      </span>
                    </div>
                  </div>

                  <div className="pi-invoice-meta">

                    <div className="pi-meta-item">
                      <span>
                        <CalendarDays
                          size={14}
                        />

                        Payout Date
                      </span>

                      <strong>
                        {
                          activeInvoice.payoutDate
                        }
                      </strong>
                    </div>

                    <div className="pi-meta-item">
                      <span>
                        <Calendar
                          size={14}
                        />

                        Period
                      </span>

                      <strong>
                        {
                          activeInvoice.period
                        }
                      </strong>
                    </div>

                  </div>
                </div>

                {/* Summary */}

                <div className="pi-invoice-summary">

                  <div className="pi-invoice-summary-item">

                    <div className="pi-invoice-summary-icon pi-summary-blue">
                      <Hash size={16} />
                    </div>

                    <div>
                      <span>
                        Total Cases
                      </span>

                      <strong>
                        {
                          activeInvoice.cases
                        }
                      </strong>
                    </div>

                  </div>

                  <div className="pi-invoice-summary-item">

                    <div className="pi-invoice-summary-icon pi-summary-green">
                      <IndianRupee
                        size={16}
                      />
                    </div>

                    <div>
                      <span>
                        Total Paid
                      </span>

                      <strong>
                        {currency(
                          activeInvoice.paid
                        )}
                      </strong>
                    </div>

                  </div>
                </div>

                {/* =================================================
                    CASE DETAILS DATATABLE
                ================================================= */}

                <div className="pi-case-section">

                  <div className="pi-case-section-heading">

                    <div>
                      <h3>
                        Case Details
                      </h3>

                      <p>
                        Case-wise payment
                        breakdown
                      </p>
                    </div>

                    <span className="pi-case-count">
                      {
                        activeInvoice.cases
                      }{" "}
                      cases
                    </span>

                  </div>

                  <div className="pi-case-datatable">

                  <DataTable
    columns={caseColumns}
    data={activeInvoice.caseDetails ?? []}
    progressPending={loadingCases}
    progressComponent={
      <div className="py-4">
        Loading case details...
      </div>
    }
    pagination
                      paginationPerPage={
                        5
                      }
                      paginationRowsPerPageOptions={[
                        5,
                        10,
                        15,
                        20,
                      ]}
                      highlightOnHover
                      responsive
                      fixedHeader
                      fixedHeaderScrollHeight="260px"
                      customStyles={
                        caseTableStyles
                      }
                      noDataComponent={
                        <div className="pi-case-empty">
                          No case details
                          available.
                        </div>
                      }
                    />

                  </div>
                </div>

                {/* Total */}

                <div className="pi-invoice-total">

                  <div>
                    <span>
                      Total payable
                    </span>

                    <small>
                      {
                        activeInvoice.cases
                      }{" "}
                      completed cases
                    </small>
                  </div>

                  <strong>
                    {currency(
                      activeInvoice.paid
                    )}
                  </strong>
                </div>

                {/* Note */}

                <div className="pi-invoice-note">

                  <CheckCircle2
                    size={15}
                  />

                  <span>
                    This invoice has
                    been settled in full.
                  </span>

                </div>
              </div>

              {/* =================================================
                  MODAL FOOTER
              ================================================= */}

              <div className="pi-invoice-actions">

                <button
                  type="button"
                  className="pi-invoice-secondary-btn"
                  onClick={() =>
                    setActiveInvoice(null)
                  }
                >
                  Close
                </button>

            <button
    type="button"
    className="pi-invoice-download-btn"
    onClick={() =>
      handleDownloadInvoice(activeInvoice)
    }
  >
    <Download size={16} />
    Download Invoice PDF
  </button>

              </div>

            </div>
          </div>
        )}
      </div>
    );
  }